<template>
  <page-header-wrapper>
    <a-space direction="vertical" style="width: 100%">
      <a-row :gutter="24">
        <a-col :span="8">
          <a-card :loading="loading" :title="$t('monitor.cacheList.cacheList')" :bordered="false">
            <a-space slot="extra">
              <a-popconfirm
                :ok-text="$t('common.yes')"
                :cancel-text="$t('common.no')"
                @confirm="handleClearCacheAll()"
              >
                <span slot="title">{{ $t('monitor.cacheList.confirmClearAll') }}</span>
                <a>
                  <a-icon type="delete" />
                </a>
              </a-popconfirm>
              <a-divider type="vertical" />
              <a @click="refreshCacheNames()">
                <a-icon type="sync" />
              </a>
            </a-space>
            <a-table
              :loading="loading"
              size="middle"
              rowKey="cacheName"
              :columns="cacheColumns"
              :data-source="cacheNames"
              :customRow="customRow"
              :pagination="false"
              :bordered="false">
              <span slot="cacheName" slot-scope="text, record">
                {{ nameFormatter(record) }}
              </span>
              <span slot="operation" slot-scope="text, record">
                <a-popconfirm
                  :ok-text="$t('common.yes')"
                  :cancel-text="$t('common.no')"
                  @confirm="handleClearCacheName(record)"
                >
                  <span slot="title">{{ $t('monitor.cacheList.confirmDelete', { name: record.cacheName }) }}</span>
                  <a><a-icon type="delete" /></a>
                </a-popconfirm>
              </span>
            </a-table>
          </a-card>
        </a-col>
        <a-col :span="8">
          <a-card :loading="loading" :title="$t('monitor.cacheList.keyList')" :bordered="false">
            <a slot="extra" @click="refreshCacheKeys()">
              <a-icon type="sync" />
            </a>
            <a-list :data-source="cacheKeys">
              <a-list-item slot="renderItem" slot-scope="item">
                <a-popconfirm
                  slot="actions"
                  :ok-text="$t('common.yes')"
                  :cancel-text="$t('common.no')"
                  @confirm="handleClearCacheKey(item)"
                >
                  <span slot="title">{{ $t('monitor.cacheList.confirmDelete', { name: item }) }}</span>
                  <a><a-icon type="delete" /></a>
                </a-popconfirm>
                <a-list-item-meta @click="handleCacheValue(item)">
                  <ellipsis slot="description" :length="35">
                    {{ keyFormatter(item) }}
                  </ellipsis>
                </a-list-item-meta>
              </a-list-item>
              <div slot="header">
                {{ nowCacheName }}
              </div>
            </a-list>
          </a-card>
        </a-col>
        <a-col :span="8">
          <a-card :loading="loading" :title="$t('monitor.cacheList.cacheContent')" :bordered="false">
            <a-descriptions size="middle" style="word-break: break-all;" layout="vertical" :column="3" bordered>
              <a-descriptions-item :label="$t('monitor.cacheList.cacheName')" :span="3">
                {{ cacheForm.cacheName }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.cacheList.cacheKey')" :span="3">
                {{ cacheForm.cacheKey }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.cacheList.cacheValue')" :span="3">
                {{ cacheForm.cacheValue }}
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>
      </a-row>
    </a-space>
  </page-header-wrapper>
</template>

<script>

import { listCacheName, listCacheKey, getCacheValue, clearCacheName, clearCacheKey, clearCacheAll } from '@/api/monitor/cache'
import { Ellipsis } from '@/components'

export default {
  name: 'CacheList',
  components: {
    Ellipsis
  },
  data () {
    return {
      loading: true,
      cacheNames: [],
      cacheKeys: [],
      cacheForm: {},
      subLoading: false,
      nowCacheName: ''
    }
  },
  created () {
    this.getCacheNames()
  },
  computed: {
    cacheColumns () {
      return [
        {
          title: this.$t('monitor.cacheList.cacheName'),
          dataIndex: 'cacheName',
          scopedSlots: { customRender: 'cacheName' },
          align: 'center'
        },
        {
          title: this.$t('system.remark'),
          dataIndex: 'remark',
          align: 'center'
        },
        {
          title: this.$t('common.operation'),
          dataIndex: 'operation',
          width: '20%',
          scopedSlots: { customRender: 'operation' },
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getCacheNames () {
      this.loading = true
      listCacheName().then(response => {
        this.cacheNames = response.data
        this.loading = false
      })
    },
    customRow (record, index) {
      const style = record.cacheName === this.nowCacheName ? {} : { opacity: 0.7 }
      return {
        on: {
          click: () => {
            this.getCacheKeys(record)
          }
        },
        style
      }
    },
    refreshCacheNames () {
      this.getCacheNames()
      this.$message.success(this.$t('monitor.cacheList.refreshNamesSuccess'), 3)
    },
    handleClearCacheName (row) {
      clearCacheName(row.cacheName).then(response => {
        this.$message.success(this.$t('monitor.cacheList.clearNameSuccess', { name: this.nowCacheName }), 3)
        this.getCacheKeys()
      })
    },
    getCacheKeys (row) {
      const cacheName = row !== undefined ? row.cacheName : this.nowCacheName
      if (cacheName === '') {
        return
      }
      this.subLoading = true
      listCacheKey(cacheName).then(response => {
        this.cacheKeys = response.data
        this.subLoading = false
        this.nowCacheName = cacheName
      })
    },
    refreshCacheKeys () {
      this.getCacheKeys()
      this.$message.success(this.$t('monitor.cacheList.refreshKeysSuccess'), 3)
    },
    handleClearCacheKey (cacheKey) {
      clearCacheKey(cacheKey).then(response => {
        this.$message.success(this.$t('monitor.cacheList.clearKeySuccess', { key: cacheKey }), 3)
        this.getCacheKeys()
      })
    },
    nameFormatter (row) {
      return row.cacheName.replace(':', '')
    },
    keyFormatter (cacheKey) {
      return cacheKey.replace(this.nowCacheName, '')
    },
    handleCacheValue (cacheKey) {
      getCacheValue(this.nowCacheName, cacheKey).then(response => {
        this.cacheForm = response.data
      })
    },
    handleClearCacheAll () {
      clearCacheAll().then(response => {
        this.$message.success(this.$t('monitor.cacheList.clearAllSuccess'), 3)
      })
    }
  }
}
</script>
